import { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { AdditionalInputField } from '@/types/checkout';
import { AdyenPayFormUI } from './AdyenPayFormUI';
import { type AdyenPaymentResponse } from '@/types/payment';
import { ProviderEnum, type PaymentMethodEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { isEmailValid } from '@/utils/email';
import { AdyenInputType } from '@/types/adyen';
import type { Address } from '@/api/address';
import { getDefaultFieldValues, buildFieldValidationRegex } from '@/utils/adyen';
import { logError } from '@/utils/logger';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

interface Props {
  selectedPaymentMethod: PaymentMethodEnum;
}

export const AdyenPayForm = ({ selectedPaymentMethod }: Props) => {
  const {
    paymentMethodsSettings,
    customer,
    actions: { runPayment, setIsFormValid },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const paymentMethod = useMemo(() => {
    return paymentMethodsSettings.find((method) => method.paymentMethodName === selectedPaymentMethod);
  }, [paymentMethodsSettings, selectedPaymentMethod]);

  const additionalInputFields = useMemo(() => {
    if (!paymentMethod) {
      return [];
    }
    const paymentMethodFields = paymentMethodsSettings.find(
      (method) => method.id === paymentMethod.id
    )?.additionalInputFields;
    return paymentMethodFields;
  }, [paymentMethodsSettings, paymentMethod]);

  const [fieldValues, setFieldValues] = useState<Record<string, string | Address>>(() =>
    getDefaultFieldValues(additionalInputFields ?? [], customer)
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [billingAddress, setBillingAddress] = useState<Address | null>(
    () => (fieldValues[AdyenInputType.BILLING_ADDRESS] as Address | undefined) ?? null
  );

  const [fieldTouched, setFieldTouched] = useState<Record<string, boolean>>(
    additionalInputFields?.reduce(
      (acc, field) => {
        acc[field.apm_key] = false;
        return acc;
      },
      {} as Record<string, boolean>
    ) ?? {}
  );

  const validateField = useCallback(
    (field: AdditionalInputField, value: string): string => {
      // Trim so a whitespace-only value (e.g. "   ") counts as missing — it is
      // truthy but the BE (and the shopperName split below) treats it as empty.
      if (!value.trim()) {
        return t('fieldErrors.incomplete', { field: field.display_name });
      }

      if (field.validation) {
        try {
          const regex = buildFieldValidationRegex(field.validation);
          if (!regex.test(value)) {
            return t('fieldErrors.invalidFormat', { field: field.display_name.toLowerCase() });
          }
        } catch (error) {
          logError('Invalid regex pattern for Adyen field', error, { apmKey: field.apm_key });
        }
      }

      // Composite free-text fields (e.g. `shopperName` -> firstName/lastName)
      // are split on whitespace before being sent to the PSP
      // (see `transformShopperName`). The BE rejects the payment when any
      // sub-item is empty ("first or last name is missing"), so mirror that
      // split here: the value must yield at least as many non-empty tokens as
      // the field declares sub-items. `billingAddress` is excluded — it is
      // collected as a structured object, not a single space-separated string.
      const subItemCount = field.items?.length ?? 0;
      if (subItemCount > 1 && field.apm_key !== AdyenInputType.BILLING_ADDRESS) {
        const tokens = value.trim().split(/\s+/).filter(Boolean);
        if (tokens.length < subItemCount) {
          return t('fieldErrors.invalidFormat', { field: field.display_name.toLowerCase() });
        }
      }

      return '';
    },
    [t]
  );

  const handlePayment = useCallback(async () => {
    await runPayment<AdyenPaymentResponse>(fieldValues, undefined, ProviderEnum.ADYEN);
  }, [fieldValues, runPayment]);

  const handleFieldChange = useCallback(
    (field: AdditionalInputField, value: string) => {
      setFieldValues((prev) => ({
        ...prev,
        [field.apm_key]: value,
      }));
      const error = validateField(field, value);

      if (error) {
        setFieldErrors((prev) => ({
          ...prev,
          [field.apm_key]: error,
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field.apm_key];
          return newErrors;
        });
      }
    },
    [validateField]
  );

  const handleAddressSelect = useCallback((field: AdditionalInputField, address: Address) => {
    setBillingAddress(address);
    setFieldValues((prev) => ({
      ...prev,
      [field.apm_key]: address,
    }));
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field.apm_key];
      return newErrors;
    });
  }, []);

  const handleFieldBlur = useCallback((field: AdditionalInputField) => {
    setFieldTouched((prev) => ({
      ...prev,
      [field.apm_key]: true,
    }));
  }, []);

  // Update global form validity
  const isFormValid = useMemo(() => {
    const fieldsWithoutEmail = additionalInputFields?.filter(
      (field) => field.apm_key !== AdyenInputType.EMAIL && field.apm_key !== AdyenInputType.SHOPPER_EMAIL
    );
    if (
      !additionalInputFields ||
      additionalInputFields.length === 0 ||
      (isEmailValid(customer?.email) && fieldsWithoutEmail?.length === 0 && isZipCodeValid)
    ) {
      return true;
    }

    // Drive validity off the same `validateField` used for error display, so
    // each field's BE `validation` regex (e.g. the Affirm full-name format) is
    // the single source of truth — a field is only "filled" when it also
    // passes its validation, not merely when it is non-empty.
    const allFieldsValid = fieldsWithoutEmail?.every((field) => {
      if (field.apm_key === AdyenInputType.BILLING_ADDRESS) {
        return billingAddress !== null;
      }
      const value = fieldValues[field.apm_key];
      if (typeof value !== 'string') {
        return false;
      }
      return validateField(field, value) === '';
    });

    return Boolean(allFieldsValid && isZipCodeValid && isEmailValid(customer?.email));
  }, [fieldValues, additionalInputFields, customer?.email, isZipCodeValid, billingAddress, validateField]);

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [isFormValid, setIsFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (!additionalInputFields || additionalInputFields.length === 0) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <AdyenPayFormUI
      handleTaxValidationChange={handleTaxValidationChange}
      additionalInputFields={additionalInputFields}
      fieldValues={fieldValues}
      fieldErrors={fieldErrors}
      fieldTouched={fieldTouched}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      onAddressSelect={handleAddressSelect}
      onPayment={handlePayment}
      isDisabled={!isFormValid || !isEmailValid(customer?.email)}
    />
  );
};
